import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  effect,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CanvasEvent, Graph, NodeEvent } from '@antv/g6';
import { TranslateService } from '@ngx-translate/core';

import { PlanCreationTrigger, PlanMetadataModel } from '@gfe/core/models/plan.model';
import { ICONS } from '@gfe/core/services/icons/icons.service';
import { isNil } from '@gfe/core/utils/shared-utils';
import { ContextMenuOption } from '@gfe/shared/components/context-menu/context-menu.component';
import { TooltipService } from '@gfe/shared/components/tooltip-singleton/tooltip.service';
import { ContextMenuService } from '@gfe/shared/services/context-menu/context-menu.service';

export interface PlanningPlanTriggerData {
  planningDurationSeconds: number;
  planningStartDate: Date;
}

export interface EventProcessingTriggerData {
  nbEvents: number;
}

export type PlanCreationTriggerData = PlanningPlanTriggerData | EventProcessingTriggerData;

export interface PlanNode {
  id: string;
  parentId: string | undefined;
  creationDate: Date;
  recipeId?: string;
  visuId?: string;
  isDeployed?: boolean;
  creationTrigger: PlanCreationTrigger;
  creationTriggerData?: PlanCreationTriggerData;
}

export interface GraphConfig {
  onNodeClick?: (planId: string) => void;
}

/**
 * Id of the hidden node used to group multiple root plans into a single tree so
 * the 'compact-box' layout does not stack them on top of each other.
 */
const GRAPH_VIRTUAL_ROOT_ID = '__plan-graph-virtual-root__';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isLeafNode(d: any) {
  return !d.children || d.children.length === 0;
}

@Component({
  selector: 'app-planning-space-graph-visu',
  imports: [],
  templateUrl: './planning-space-graph-visu.component.html',
  styles: ``
})
export class PlanningSpaceGraphVisuComponent implements AfterViewInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  private readonly tooltipService = inject(TooltipService);
  private readonly contextMenuService = inject(ContextMenuService);

  protected readonly ICONS = ICONS;

  config = input<GraphConfig>({});
  planNodes = input<PlanNode[]>([]);
  planMetadata = input<PlanMetadataModel[]>([]);

  planMetadataDelete = output<string>();
  planNodeClick = output<string>();
  planDuplicate = output<string>();
  planRename = output<string>();

  planId = signal('');
  showMenuOnRightClick = signal(false);

  protected currentLang = toSignal(this.translate.onLangChange);

  private graph!: Graph;
  @ViewChild('graphContainer', { static: false }) graphContainer!: ElementRef;
  @ViewChild('graphElement', { static: false }) graphElement!: ElementRef;
  private resizeObserver?: ResizeObserver;
  private virtualTooltipElement?: HTMLElement;
  private tooltipCloseTimeout?: NodeJS.Timeout;
  isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;

  constructor() {
    // An empty list is a state in its own right — deleting a root plan takes its whole
    // subtree with it, so it must clear the canvas rather than leave the last render up.
    effect(() => {
      const nodes = this.planNodes();
      if (this.graph) {
        this.updateGraph(nodes);
      }
    });
  }

  ngAfterViewInit(): void {
    this.initializeGraph();
    const nodes = this.planNodes();
    if (nodes.length > 0) {
      this.updateGraph(nodes);
    }
    this.setupResizeObserver();
    this.registerContextMenu();
  }

  registerContextMenu(): void {
    // The right-clicked node id is captured by `handleNodeRightClick`, which
    // also flips the element's `data-context` to 'planning-space-graph-menu'.
    // The handler reads `planId()` lazily when the global context menu asks for
    // options, and delegates the actual flows to the parent.
    this.contextMenuService.register('planning-space-graph-menu', () => {
      const planId = this.planId();
      if (!planId) {
        return [];
      }
      const options: ContextMenuOption[] = [
        {
          label: this.translate.instant('common.duplicate'),
          icon: ICONS.copy,
          action: () => this.planDuplicate.emit(planId)
        },
        {
          label: this.translate.instant('common.rename'),
          icon: ICONS.pencil,
          action: () => this.planRename.emit(planId)
        },
        {
          label: this.translate.instant('common.delete'),
          icon: ICONS.trash,
          isDanger: true,
          action: () => this.planMetadataDelete.emit(planId)
        }
      ];
      return options;
    });
  }

  ngOnDestroy(): void {
    this.hideTooltip();
    if (this.tooltipCloseTimeout) {
      clearTimeout(this.tooltipCloseTimeout);
    }
    if (this.graph) {
      this.graph.destroy();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.virtualTooltipElement) {
      this.virtualTooltipElement.remove();
    }
  }

  private deferToNextTick(callback: () => void): void {
    setTimeout(callback, 0);
  }

  private setupResizeObserver() {
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        // Defer execution to the next event loop tick to prevent ResizeObserver
        // from firing while DOM manipulation is still in progress (e.g., during
        // divider dragging). This ensures Mapbox GL's canvas resize operation
        // occurs after all DOM updates have been flushed and layout is stable.
        this.deferToNextTick(() => {
          this.graph.resize();
        });
      });
      this.resizeObserver.observe(this.graphContainer.nativeElement);
    } else {
      // Fallback for older browsers
      window.addEventListener('resize', () => {
        this.graph.resize();
      });
    }
  }

  private initializeGraph(): void {
    if (!this.graphContainer) return;

    // Create a minimal working graph with G6 v5
    this.graph = new Graph({
      container: this.graphElement.nativeElement,
      autoFit: 'view',
      behaviors: ['drag-canvas', 'zoom-canvas', 'click-select'],
      node: {
        style: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          labelText: (d: any) => {
            const nodeData = d.data?.['nodeData'];
            return nodeData?.visuId || d.id;
          },
          labelPlacement: (d) => (isLeafNode(d) ? 'right' : 'left'),
          labelBackground: true,
          labelBackgroundFill: this.isDarkMode ? '#18181b' : '#ffffff',
          labelBackgroundOpacity: 0.95,
          labelBackgroundRadius: 4,
          labelFill: this.isDarkMode ? '#e4e4e7' : '#27272a',
          labelFontSize: 12,
          labelPadding: [2, 6],
          size: 20,
          lineWidth: 1,
          cursor: 'pointer',
          ports: [{ placement: 'right' }, { placement: 'left' }]
        },
        state: {
          selected: {
            lineWidth: 2
          },
          active: {
            lineWidth: 2
          }
        },
        animation: {
          enter: false
        }
      },
      edge: {
        type: 'cubic-horizontal',
        style: {
          lineWidth: 1,
          stroke: this.isDarkMode ? '#52525b' : '#a1a1aa',
          endArrow: true,
          endArrowFill: this.isDarkMode ? '#52525b' : '#a1a1aa',
          endArrowSize: 6
        },
        animation: {
          enter: false
        }
      },
      layout: {
        type: 'compact-box',
        direction: 'LR',
        getHeight: function getHeight() {
          return 16;
        },
        getWidth: function getWidth() {
          return 16;
        },
        getVGap: function getVGap() {
          return 40;
        },
        getHGap: function getHGap() {
          return 100;
        }
      },
      data: {
        nodes: [],
        edges: []
      }
    });

    // Register event handlers
    this.graph.on(NodeEvent.DBLCLICK, (event) => this.handleNodeDoubleClick(event));
    this.graph.on(NodeEvent.CLICK, (event) => this.handleNodeClick(event));
    this.graph.on(NodeEvent.CONTEXT_MENU, (event) => this.handleNodeRightClick(event));
    this.graph.on(CanvasEvent.CONTEXT_MENU, () => {
      this.handleCanvasRightClick();
    });

    // Add tooltip event handlers
    this.graph.on(NodeEvent.POINTER_ENTER, (event) => this.handleNodePointerEnter(event));
    this.graph.on(NodeEvent.POINTER_LEAVE, () => this.handleNodePointerLeave());
    this.graph.on(CanvasEvent.POINTER_MOVE, () => this.handleCanvasPointerMove());
  }

  private updateGraph(nodes: PlanNode[]): void {
    if (!this.graph) return;
    const metadata = this.planMetadata();
    const nodeIds = new Set(nodes.map((n) => n.id));

    const g6Nodes = nodes.map((node) => {
      const nodeMetadata = metadata.find((m) => m.id === node.id);
      let color = this.getNodeColor(node.creationTrigger);
      let strokeColor = color;
      if (node.isDeployed) {
        color = this.getNodeColor('IsDeployed');
        strokeColor = color;
      }
      return {
        id: node.id,
        data: {
          nodeData: node,
          metadata: nodeMetadata
        },
        style: {
          fill: color,
          stroke: strokeColor,
          radius: 4
        }
      };
    });

    // A duplication edge is drawn dashed in the 'Duplication' color so a copy
    // reads as "copied from" its source rather than an optimization result
    // (solid arrow). The backend gives a duplicate the same parentPlanId link as
    // an optimization child, so the edge exists; only its styling differs.
    const duplicationColor = this.getNodeColor('Duplication');
    const g6Edges = nodes
      .filter((node) => node.parentId && nodeIds.has(node.parentId))
      .map((node) => {
        const isDuplication = node.creationTrigger === 'Duplication';
        return {
          id: `${node.parentId}-${node.id}`,
          source: node.parentId!,
          target: node.id,
          data: {
            triggerData: node.creationTriggerData,
            creationTrigger: node.creationTrigger
          },
          ...(isDuplication
            ? {
                style: {
                  lineDash: [4, 4],
                  stroke: duplicationColor,
                  endArrowFill: duplicationColor
                }
              }
            : {})
        };
      });

    // The 'compact-box' layout is a single-root tree layout; G6 lays out each
    // root from the same origin, so a forest (e.g. several initial plans, or a
    // duplicate — which the backend creates as a parent-less root) would render
    // with all root trees stacked on top of each other. Attach every real root
    // to a single hidden virtual root so the whole graph is one tree and the
    // roots are spread out as siblings instead of overlapping.
    const rootNodes = nodes.filter((node) => !node.parentId || !nodeIds.has(node.parentId));

    let finalNodes = g6Nodes as unknown[];
    let finalEdges = g6Edges as unknown[];
    if (rootNodes.length > 1) {
      finalNodes = [
        ...g6Nodes,
        {
          id: GRAPH_VIRTUAL_ROOT_ID,
          data: { nodeData: null, metadata: undefined },
          style: { visibility: 'hidden', size: 1 }
        }
      ];
      finalEdges = [
        ...g6Edges,
        ...rootNodes.map((root) => ({
          id: `${GRAPH_VIRTUAL_ROOT_ID}-${root.id}`,
          source: GRAPH_VIRTUAL_ROOT_ID,
          target: root.id,
          data: {},
          style: { visibility: 'hidden' }
        }))
      ];
    }

    // Update graph data
    this.graph.setData({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      nodes: finalNodes as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      edges: finalEdges as any
    });

    // Render is async but we don't need to wait for it
    void this.graph.render();
  }

  private getNodeColor(trigger: PlanCreationTrigger): string {
    const colors = {
      // Atoptima primary teal
      Initial: '#284E5C',
      // Custom purple (#59168B) — deployed plans use the accent purple
      IsDeployed: '#59168B',
      // Tailwind zinc-500
      Optimize: '#71717a',
      // Tailwind blue-500
      Recompute: '#3b82f6',
      // Tailwind amber-500
      EventProcessing: '#f59e0b',
      // Tailwind amber-500
      Duplication: '#347d98'
    };
    const darkColors = {
      // Atoptima primary teal light
      Initial: '#3F7A91',
      // Custom purple (#59168B) — deployed plans use the accent purple
      IsDeployed: '#59168B',
      // Tailwind zinc-400
      Optimize: '#a1a1aa',
      // Tailwind blue-400
      Recompute: '#60a5fa',
      // Tailwind amber-400
      EventProcessing: '#fbbf24',
      // Tailwind amber-400
      Duplication: '#559db7'
    };
    if (this.isDarkMode) {
      return darkColors[trigger] || '#60a5fa';
    } else {
      return colors[trigger] || '#3b82f6';
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleNodeClick(event: any) {
    const { target } = event;
    const planId = target.id;
    if (planId === GRAPH_VIRTUAL_ROOT_ID) return;
    this.planNodeClick.emit(planId);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleNodeDoubleClick(event: any) {
    const { target } = event;
    const planId = target.id;
    if (planId === GRAPH_VIRTUAL_ROOT_ID) return;
    const onNodeClick = this.config().onNodeClick;
    if (!isNil(onNodeClick)) {
      onNodeClick(planId);
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleNodeRightClick(event: any) {
    const { target } = event;
    if (target.id === GRAPH_VIRTUAL_ROOT_ID) return;
    // Right-click does not fire pointer-leave, so the hover tooltip would linger
    // over the context menu and the modal it opens. Hide it now.
    if (this.tooltipCloseTimeout) {
      clearTimeout(this.tooltipCloseTimeout);
      this.tooltipCloseTimeout = undefined;
    }
    this.hideTooltip();
    this.planId.set(target.id);
    this.showMenuOnRightClick.set(true);
    this.cdr.detectChanges();
    this.registerContextMenu();
  }

  handleCanvasRightClick() {
    this.showMenuOnRightClick.set(false);
    this.planId.set('');
    this.cdr.detectChanges();
    this.registerContextMenu();
  }

  // Tooltip handling methods
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleNodePointerEnter(event: any) {
    if (this.tooltipCloseTimeout) {
      clearTimeout(this.tooltipCloseTimeout);
      this.tooltipCloseTimeout = undefined;
    }

    const { target } = event;
    const nodeId = target.id;
    const nodeData = this.planNodes().find((n) => n.id === nodeId);
    const metadata = this.planMetadata().find((m) => m.id === nodeId);

    if (!nodeData || !metadata) return;

    // Create or update virtual tooltip element
    if (!this.virtualTooltipElement) {
      this.virtualTooltipElement = document.createElement('div');
      this.virtualTooltipElement.style.position = 'absolute';
      this.virtualTooltipElement.style.pointerEvents = 'none';
      this.virtualTooltipElement.style.zIndex = '9999';
      document.body.appendChild(this.virtualTooltipElement);
    }

    // Position the virtual element at the node's screen position
    this.virtualTooltipElement.style.left = `${event.client.x}px`;
    this.virtualTooltipElement.style.top = `${event.client.y}px`;

    const title = metadata.name;
    const content = this.getTooltipContent(nodeData);

    this.tooltipService.show(this.virtualTooltipElement, content, {
      title,
      position: 'top',
      offset: 12,
      interactive: false
    });
  }

  private handleNodePointerLeave() {
    this.tooltipCloseTimeout = setTimeout(() => {
      this.hideTooltip();
    }, 100);
  }

  private handleCanvasPointerMove() {
    // Hide tooltip when moving on canvas (not on nodes)
    if (this.tooltipCloseTimeout) {
      clearTimeout(this.tooltipCloseTimeout);
    }
    this.tooltipCloseTimeout = setTimeout(() => {
      this.hideTooltip();
    }, 100);
  }

  private hideTooltip() {
    this.tooltipService.hide();
  }

  private getTooltipContent(nodeData: PlanNode): string {
    const content: string[] = [];

    // Helper function to make label bold
    const makeLabelBold = (text: string): string => {
      const parts = text.split(':');
      if (parts.length >= 2) {
        return `<strong>${parts[0]}:</strong>${parts.slice(1).join(':')}`;
      }
      return text;
    };

    // Creation date
    const createdDate = nodeData.creationDate.toLocaleString(
      this.translate.getCurrentLang(),
      {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }
    );
    content.push(
      makeLabelBold(
        this.translate.instant('features.planning_space_visu.created', {
          items: createdDate
        })
      )
    );

    // Trigger
    content.push(
      makeLabelBold(
        this.translate.instant('features.planning_space_visu.trigger', {
          items: nodeData.creationTrigger
        })
      )
    );

    // Recipe or Parameters
    if (nodeData.recipeId) {
      content.push(
        makeLabelBold(
          this.translate.instant('features.planning_space_visu.recipe', {
            items: nodeData.recipeId
          })
        )
      );
    } else if (nodeData.visuId) {
      content.push(
        makeLabelBold(
          this.translate.instant('features.planning_space_visu.parameters', {
            items: nodeData.visuId
          })
        )
      );
    }

    // Deployed status
    if (nodeData.isDeployed) {
      content.push(
        `<span class="text-purple-600 dark:text-purple-500 font-semibold">${this.translate.instant('features.planning_space_visu.deploy')}</span>`
      );
    }

    return content.join('<br>');
  }
}
